import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Recurso } from '../../api/ops/pagos';
import type { FotoSala, Plano, ReservaEnVivo, VersionPlano } from '../../api/ops/sala';
import type { Reserva } from '../../api/ops/reservas';
import { claveIdempotencia } from '../../api/operaciones';
import { ErrorApi } from '../../api/problema';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Campo } from '../../componentes/Campo';
import { Chip } from '../../componentes/Chip';
import { EstadoCargando, EstadoError, EstadoNoContratado, EstadoSinPermiso, EstadoVacia } from '../../componentes/EstadoPagina';
import { Icono } from '../../componentes/Icono';
import { Segmentos } from '../../componentes/Segmentos';
import { Ventana } from '../../componentes/Ventana';
import { rolesDe } from '../../logica/acceso';
import { avisoBreveDeError, estadoDeError } from '../../logica/estados';
import { conVocabulario, useCabecera } from '../../marco/cabecera';
import { usePermisos } from '../../permisos/permisos';
import type { PropsPantalla } from '../tipos';
import { nombreDe, useContextoReservas, useNombres } from '../reservas/contexto';
import { hoyEn, horaEn } from '../reservas/fechas';
import { mayuscula } from '../reservas/textos';
import { VentanaConfirmar, VentanaEscanear, VentanaReten, WalkIn, type Confirmacion } from './Dialogos';
import { HojaOcupacion } from './HojaOcupacion';
import { PlanoSala, type RecursoEnPlano } from './PlanoSala';
import { PERMISOS_SALA as P, aDesplazar, cabe, capacidadJunta, chipDeReserva, cifrasLeyenda, esPorLlegar, estadoPintado, libresPara, listaDeLlegadas, relativo, retenDe, sePuedenJuntar, textoSentadaSinPagar } from './logica';
import { useSalaEnVivo } from './tiempoReal';
import './sala.css';

/**
 * 8.7 Sala en vivo (Operativa, entrada `sala`, bloque Plano y sala; tablet principal). Salas del plano en segmentos, la
 * lista de llegadas a la izquierda («Por llegar», «Sin reservado» o todas), el plano con el estado escrito de cada recurso,
 * «En directo» o «Cada 30 s» (G463) y las acciones: sentar (con llegada por `validatePass` y el `pass_id` de la foto,
 * G797), solo asignar, juntar (G396), desplazar (G397, Encargado y más), sentar igualmente (G16), forzar capacidad con
 * motivo, walk-in (G399) y retenes (G462). Sin «Se ha ido» (G794). «Imprimir» abre la hoja de ocupación (también con
 * `?imprimir=1&dia=` desde 8.1, G796).
 */
type Filtro = 'por-llegar' | 'sin-reservado' | 'todas';
const FILTROS: readonly { valor: Filtro; texto: string }[] = [
    { valor: 'por-llegar', texto: 'Por llegar' },
    { valor: 'sin-reservado', texto: 'Sin reservado' },
    { valor: 'todas', texto: 'Todas' },
];

interface Datos {
    plano: Plano | null;
    foto: FotoSala | null;
    version: VersionPlano | null;
    recursos: Recurso[];
}

async function todosLosRecursos(ops: PropsPantalla['ops'], negocio: string): Promise<Recurso[]> {
    const lista: Recurso[] = [];
    let cursor: string | null = null;
    for (let i = 0; i < 20; i += 1) {
        const p = await ops.listResources(negocio, { cursor, limit: 100 });
        lista.push(...p.data);
        cursor = p.next_cursor;
        if (cursor === null) {
            break;
        }
    }
    return lista;
}

export function Sala(props: PropsPantalla) {
    const { ops, negocio, movil, soloLectura, membresia, nombreNegocio, navegar, busqueda } = props;
    const permisos = usePermisos();
    const avisar = useAvisoBreve();
    const contexto = useContextoReservas(ops, negocio, membresia, nombreNegocio);
    const palabra = contexto.palabra;
    const hoy = hoyEn(contexto.zona);
    const diaPedido = busqueda.get('dia');
    const dia = diaPedido !== null && /^\d{4}-\d{2}-\d{2}$/.test(diaPedido) ? diaPedido : hoy;
    const [imprimir, setImprimir] = useState(busqueda.get('imprimir') === '1');
    const [datos, setDatos] = useState<Datos | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [intento, setIntento] = useState(0);
    const [sala, setSala] = useState(0);
    const [filtro, setFiltro] = useState<Filtro>('por-llegar');
    const [buscando, setBuscando] = useState(false);
    const [texto, setTexto] = useState('');
    const [elegida, setElegida] = useState<string | null>(null);
    const [elegidos, setElegidos] = useState<readonly string[]>([]);
    const [fichaCargada, setFicha] = useState<{ id: string; datos: Reserva | null } | null>(null);
    const [zoom, setZoom] = useState(1);
    const [walkIn, setWalkIn] = useState<{ recurso: string | null } | null>(null);
    const [reten, setReten] = useState<{ recurso: Recurso; error: string | null } | null>(null);
    const [recursoAbierto, setRecursoAbierto] = useState<RecursoEnPlano | null>(null);
    const [escanear, setEscanear] = useState(false);
    const [lectura, setLectura] = useState<{ bien: boolean; texto: string } | null>(null);
    const [confirmar, setConfirmar] = useState<{ c: Confirmacion; sentar: boolean; opciones: Opciones } | null>(null);
    const [ocupado, setOcupado] = useState(false);
    const [ahora, setAhora] = useState(() => new Date());
    const ver = permisos.puede(P.ver);
    const escribe = !soloLectura;
    const puede = {
        asignar: escribe && permisos.puede(P.asignar),
        llegada: escribe && permisos.puede(P.llegada),
        walkIn: escribe && permisos.puede(P.crear),
        retenes: escribe && permisos.puede(P.retenes),
        desplazar: escribe && permisos.puede(P.desplazar),
        forzar: escribe && permisos.puede(P.forzar),
    };

    const recargar = useCallback(() => {
        setIntento((i) => i + 1);
    }, []);

    useEffect(() => {
        if (!ver) {
            return;
        }
        let vivo = true;
        (async () => {
            const planos = await ops.listFloorPlans(negocio, { limit: 50 });
            const plano = planos.data.find((p) => p.status === 'active' && p.effective_version != null) ?? null;
            if (plano === null) {
                return { plano: null, foto: null, version: null, recursos: [] };
            }
            const [foto, recursos] = await Promise.all([ops.getFloorLiveState(negocio, plano.id, dia), todosLosRecursos(ops, negocio)]);
            return { plano, foto, recursos };
        })()
            .then(async (d) => {
                const anterior = datos?.version;
                const version = d.foto === null ? null : anterior?.id === d.foto.version_id ? anterior : await ops.getFloorPlanVersion(negocio, d.foto.version_id);
                if (vivo) {
                    setDatos({ plano: d.plano, foto: d.foto, recursos: d.recursos, version });
                    setError(null);
                    setAhora(new Date());
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setError(e);
                }
            });
        return () => {
            vivo = false;
        };
        // La versión del plano se reutiliza si no cambia: no es una dependencia de la carga.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ops, negocio, dia, intento, ver]);

    const conexion = useSalaEnVivo(ops, negocio, datos?.plano?.id ?? null, recargar);
    const foto = datos?.foto ?? null;
    const recursos = useMemo(() => datos?.recursos ?? [], [datos]);
    const nombres = useNombres(ops, negocio, (foto?.reservations ?? []).map((r) => r.customer_id).filter((x): x is string => typeof x === 'string'), permisos.puede(P.clientes));
    const reserva = foto?.reservations.find((r) => r.reservation_id === elegida) ?? null;
    const ficha = fichaCargada?.id === elegida ? fichaCargada.datos : null;

    // La reserva elegida, entera (alergias mientras dura, G192).
    useEffect(() => {
        if (elegida === null || !permisos.puede(P.reservas)) {
            return;
        }
        let vivo = true;
        ops.getReservation(negocio, elegida)
            .then((x) => {
                if (vivo) {
                    setFicha({ id: elegida, datos: x.datos });
                }
            })
            .catch(() => {
                if (vivo) {
                    setFicha({ id: elegida, datos: null });
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, elegida, permisos]);

    useCabecera({
        ayuda: [
            { icono: 'armchair', titulo: 'Sentar', texto: `Elige una reserva de la lista y toca su ${palabra} o uno libre del plano. Para un grupo grande, toca varios y se juntan.` },
            { icono: 'door-open', titulo: 'Retenes', texto: `Guarda un ${palabra} para la gente que llega sin reserva: la web deja de venderlo hasta la hora que digas.` },
            { icono: 'refresh-cw', titulo: 'En directo', texto: 'La Sala se actualiza sola. Si se cae la conexión, se lee cada 30 segundos y lo verás arriba.' },
        ],
        acciones: [],
        subtitulo: conVocabulario('Tus {recursos} en vivo: llegadas, walk-ins y retenes.', palabra),
    });

    if (!ver) {
        return <EstadoSinPermiso seccion="Sala" rol={membresia === undefined ? '' : rolesDe(membresia)} negocio={nombreNegocio} onInicio={() => { navegar('/'); }} movil={movil} />;
    }
    if (error !== null && datos === null) {
        const e = estadoDeError(error);
        if (e.tipo === 'sin-permiso') {
            return <EstadoSinPermiso seccion="Sala" rol={membresia === undefined ? '' : rolesDe(membresia)} negocio={nombreNegocio} onInicio={() => { navegar('/'); }} movil={movil} />;
        }
        if (e.tipo === 'no-contratado') {
            return <EstadoNoContratado bloque="Plano y sala" incluye={['Plano de tus salas', 'Sala en vivo: sentar, juntar y walk-ins', 'Retenes para la puerta']} propietario={membresia?.is_owner === true} onAnadir={null} onInicio={() => { navegar('/'); }} movil={movil} />;
        }
        return <EstadoError seccion="Sala" codigoSoporte={e.codigoSoporte} onReintentar={recargar} movil={movil} />;
    }
    if (datos === null) {
        return <EstadoCargando seccion="Sala" />;
    }
    if (datos.plano === null || foto === null || datos.version === null) {
        return (
            <EstadoVacia
                seccion="Sala"
                accion={permisos.puede(P.plano) ? (
                    <button type="button" className="rv-b rv-b-sec" onClick={() => { navegar('/plano'); }}>
                        <Icono nombre="map" />
                        <span>Dibujar el plano</span>
                    </button>
                ) : undefined}
            />
        );
    }

    const version = datos.version;
    const salas = [...version.content.rooms].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    const salaActual = salas[Math.min(sala, salas.length - 1)];
    const refSala = salaActual?.room_id ?? salaActual?.name ?? '';
    const vivos = new Map(foto.resources.map((x) => [x.resource_id, x]));
    const enPlano: RecursoEnPlano[] = version.content.positions
        .filter((p) => p.room_ref === refSala || p.room_ref === salaActual?.name)
        .map((p) => {
            const recurso = recursos.find((x) => x.id === p.resource_id);
            const vivo = vivos.get(p.resource_id);
            return { posicion: p, recurso, estado: estadoPintado(vivo, recurso, foto, ahora), proxima: vivo?.next_reservation_at ?? null };
        });
    const leyenda = cifrasLeyenda(enPlano.map((r) => r.estado));
    const lista = listaDeLlegadas(foto, filtro, texto, nombres);
    const cifraFiltro = (f: Filtro) => listaDeLlegadas(foto, f).length;
    const codigo = (id: string) => {
        const rc = recursos.find((x) => x.id === id);
        return `${mayuscula(palabra)} ${rc?.code ?? ''}`;
    };
    const codigos = (ids: readonly string[]) => ids.map(codigo).join(' + ');
    const nombreZona = (id: string) => contexto.zonas.find((z) => z.id === id)?.name ?? '';
    const elegidosRec = elegidos.map((id) => recursos.find((x) => x.id === id)).filter((x): x is Recurso => x !== undefined);
    const encajan = new Set(reserva === null ? [] : libresPara(foto, recursos, reserva.party_size).map((r) => r.id));

    const salir = () => {
        setElegida(null);
        setElegidos([]);
    };

    // ---- Sentar y asignar ----
    async function asignarYSentar(r: ReservaEnVivo, ids: readonly string[], sentar: boolean, opciones: Opciones): Promise<void> {
        const cambia = ids.length > 0 && (ids.length !== (r.resource_ids ?? []).length || ids.some((x) => !(r.resource_ids ?? []).includes(x)));
        if (cambia) {
            const desplazadas = aDesplazar(foto as FotoSala, ids, r.reservation_id);
            if (desplazadas.length > 0 && opciones.desplazar !== true) {
                if (!puede.desplazar) {
                    avisar({ tipo: 'error', texto: `${codigos(ids)} está ocupado. Para quitárselo a otra reserva hace falta el Encargado.` });
                    return;
                }
                setConfirmar({ c: { tipo: 'desplazar', codigos: codigos(ids), desplazadas, nombres, zona: contexto.zona }, sentar, opciones });
                return;
            }
            const clave = opciones.clave ?? claveIdempotencia();
            try {
                await ops.assignReservationResources(negocio, r.reservation_id, {
                    resource_ids: [...ids],
                    ...(desplazadas.length > 0 ? { displace_reservation_ids: desplazadas.map((x) => x.reservation_id) } : {}),
                    confirm_different_resource: opciones.igualmente === true,
                    ...(opciones.motivo === undefined ? {} : { override: { kind: 'capacity' as const, reason: opciones.motivo }, reason: opciones.motivo }),
                }, clave);
            } catch (e) {
                if (e instanceof ErrorApi && e.codigo === 'confirmation_required') {
                    setConfirmar({ c: { tipo: 'igualmente', texto: `${codigos(ids)} no es lo que reservó (otra zona o un ${palabra} distinto). Si le sientas aquí, queda en el historial.` }, sentar, opciones: { ...opciones, igualmente: true } });
                    return;
                }
                if (e instanceof ErrorApi && e.codigo === 'capacity_unavailable' && puede.forzar && opciones.motivo === undefined) {
                    setConfirmar({ c: { tipo: 'capacidad', codigos: codigos(ids) }, sentar, opciones });
                    return;
                }
                throw e;
            }
        }
        if (sentar && esPorLlegar(r)) {
            if (r.pass_id == null && r.status === 'pending_payment') {
                // G849: con el pago pendiente aún no hay pase; se sienta igual (la mesa ya está asignada) y se avisa al
                // encargado para que lo cobre: el servicio no se para.
                avisar({ tipo: 'ok', texto: textoSentadaSinPagar(nombreDe(r, nombres)) });
                salir();
                return;
            }
            if (r.pass_id == null) {
                avisar({ tipo: 'error', texto: 'Esta reserva no tiene pase: no se puede registrar la llegada.' });
            } else {
                const l = await ops.validatePass(negocio, { method: 'manual', pass_id: r.pass_id, client_scan_id: claveIdempotencia() });
                if (l.result !== 'green') {
                    avisar({ tipo: 'error', texto: l.message ?? 'No se ha podido registrar la llegada.' });
                    return;
                }
            }
        }
        avisar({ tipo: 'ok', texto: sentar ? `${nombreDe(r, nombres)}, sentada.` : `${codigos(ids)} asignado.` });
        salir();
    }

    const lanzar = (r: ReservaEnVivo, ids: readonly string[], sentar: boolean, opciones: Opciones = {}) => {
        if (ids.length > 1 && !sePuedenJuntar(elegidosRec)) {
            avisar({ tipo: 'error', texto: `Solo se juntan ${palabra}s unibles de la misma zona.` });
            return;
        }
        setOcupado(true);
        asignarYSentar(r, ids, sentar, opciones)
            .catch((e: unknown) => {
                avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? (e instanceof ErrorApi ? e.message : 'No se ha podido sentar. Inténtalo otra vez.') });
            })
            .finally(() => {
                setOcupado(false);
                recargar();
            });
    };

    const tocarRecurso = (rp: RecursoEnPlano) => {
        const id = rp.posicion.resource_id;
        if (reserva !== null && puede.asignar) {
            setElegidos((x) => (x.includes(id) ? x.filter((y) => y !== id) : [...x, id]));
            return;
        }
        const ocupa = vivos.get(id)?.current_reservation_id;
        if (ocupa != null) {
            setElegida(ocupa);
            setElegidos([]);
            return;
        }
        setRecursoAbierto(rp);
    };

    const quitarReten = (id: string) => {
        setOcupado(true);
        ops.releaseDoorHold(negocio, id)
            .then(() => {
                avisar({ tipo: 'ok', texto: 'Retén quitado.' });
                setRecursoAbierto(null);
            })
            .catch((e: unknown) => { avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? 'No se ha podido quitar el retén.' }); })
            .finally(() => {
                setOcupado(false);
                recargar();
            });
    };

    const leer = (datosLectura: { method: 'qr'; qr_payload: string } | { method: 'typed_code'; code: string }) => {
        setOcupado(true);
        setLectura(null);
        ops.validatePass(negocio, { ...datosLectura, client_scan_id: claveIdempotencia() })
            .then((l) => {
                setLectura({ bien: l.result === 'green', texto: l.message ?? (l.result === 'green' ? 'Llegada registrada.' : 'No se ha podido registrar la llegada.') });
                recargar();
            })
            .catch((e: unknown) => { setLectura({ bien: false, texto: avisoBreveDeError(e) ?? (e instanceof ErrorApi ? e.message : 'No se ha podido leer el pase.') }); })
            .finally(() => { setOcupado(false); });
    };

    const ids = elegidos.length > 0 ? elegidos : (reserva?.resource_ids ?? []);
    const fila = (r: ReservaEnVivo) => {
        const chip = chipDeReserva(r, ahora);
        const donde = codigos(r.resource_ids ?? []);
        return (
            <li key={r.reservation_id}>
                <button type="button" className={`rv-sl-fila${elegida === r.reservation_id ? ' rv-sl-fila--elegida' : ''}`} aria-pressed={elegida === r.reservation_id} onClick={() => { setElegida(elegida === r.reservation_id ? null : r.reservation_id); setElegidos([]); }}>
                    <span className="rv-sl-fila__hora">
                        <strong>{horaEn(r.starts_at, contexto.zona)}</strong>
                        <small>{relativo(r.starts_at, ahora)}</small>
                    </span>
                    <span className="rv-sl-fila__texto">
                        <strong>{nombreDe(r, nombres)}</strong>
                        <small>{[`${String(r.party_size)} pers.`, donde].filter((x) => x !== '').join(' • ')}</small>
                    </span>
                    {chip !== null && <Chip estado={chip.estado}>{chip.texto}</Chip>}
                </button>
            </li>
        );
    };

    const capacidad = capacidadJunta(elegidosRec);
    return (
        <div className="rv-sl">
            <div className="rv-sl-barra">
                {salas.length > 1 && (
                    <Segmentos etiqueta="Salas" movil={movil} items={salas.map((s) => ({ etiqueta: s.name, icono: 'layout-grid' as const }))} valor={Math.min(sala, salas.length - 1)} onCambio={(i) => { setSala(i); }} />
                )}
                <div className="rv-sl-filtro">
                    <button type="button" className="rv-sl-filtro__pastilla" aria-label={`Lista: ${FILTROS.find((f) => f.valor === filtro)?.texto ?? ''}, ${String(cifraFiltro(filtro))}`}>
                        <span>{FILTROS.find((f) => f.valor === filtro)?.texto}</span>
                        <b>{cifraFiltro(filtro)}</b>
                    </button>
                    <button type="button" className="rv-sl-redondo rv-sl-redondo--chico" aria-label="Cambiar la lista" onClick={() => { setFiltro(FILTROS[(FILTROS.findIndex((f) => f.valor === filtro) + 1) % FILTROS.length]?.valor ?? 'todas'); }}>
                        <Icono nombre="chevron-right" />
                    </button>
                </div>
                <div className="rv-sl-herramientas">
                    {!movil && (
                        <button type="button" className="rv-sl-redondo" aria-label="Imprimir la ocupación" onClick={() => { setImprimir(true); }}>
                            <Icono nombre="printer" />
                        </button>
                    )}
                    <button type="button" className="rv-sl-redondo" aria-label="Buscar en la lista" aria-pressed={buscando} onClick={() => { setBuscando((b) => !b); setTexto(''); }}>
                        <Icono nombre="search" />
                    </button>
                    {puede.llegada && (
                        <button type="button" className="rv-b rv-b-sec" onClick={() => { setLectura(null); setEscanear(true); }}>
                            <Icono nombre="scan-line" />
                            <span>Escanear</span>
                        </button>
                    )}
                    {puede.walkIn && (
                        <button type="button" className="rv-b rv-b-pri" onClick={() => { setWalkIn({ recurso: null }); }}>
                            <Icono nombre="user-plus" />
                            <span>Walk-in</span>
                        </button>
                    )}
                </div>
            </div>
            {soloLectura && <p className="rv-sl-nota">El negocio está en solo lectura: ves la Sala, pero no puedes sentar, juntar ni guardar nada.</p>}
            <div className={`rv-sl-cuerpo${movil ? ' rv-sl-cuerpo--movil' : ''}`}>
                <section className="rv-sl-lista" aria-label="Llegadas">
                    {buscando && <Campo etiqueta="Nombre o localizador" tipo="search" valor={texto} onChange={setTexto} autoFocus />}
                    {lista.length === 0 ? (
                        <p className="rv-sl-nota rv-sl-nota--centro">{filtro === 'por-llegar' ? 'Nadie por llegar.' : filtro === 'sin-reservado' ? `Todas tienen ${palabra}.` : 'No hay reservas en esta jornada.'}</p>
                    ) : (
                        <ul>{lista.map(fila)}</ul>
                    )}
                </section>
                <section className="rv-sl-zona" aria-label="Plano">
                    {reserva === null ? (
                        <div className="rv-sl-leyenda">
                            <span><i className="rv-sl-punto rv-sl-punto--libre" /><b>{leyenda.libres}</b> libres</span>
                            <span><i className="rv-sl-punto rv-sl-punto--reservado" /><b>{leyenda.reservadas}</b> reservadas</span>
                            <span><i className="rv-sl-punto rv-sl-punto--sentado" /><b>{leyenda.sentadas}</b> sentadas</span>
                            <span><i className="rv-sl-punto rv-sl-punto--reten" /><b>{leyenda.retenes}</b> {leyenda.retenes === 1 ? 'retén' : 'retenes'}</span>
                            <span className={`rv-sl-conexion${conexion === 'directo' ? '' : ' rv-sl-conexion--sondeo'}`} role="status">
                                {conexion === 'directo' ? 'En directo' : 'Cada 30 s'}
                            </span>
                            <span className="rv-sl-zoom">
                                <button type="button" aria-label="Alejar" onClick={() => { setZoom((z) => Math.max(0.6, z - 0.2)); }}><Icono nombre="zoom-out" /></button>
                                <button type="button" aria-label="Acercar" onClick={() => { setZoom((z) => Math.min(2, z + 0.2)); }}><Icono nombre="zoom-in" /></button>
                                <button type="button" aria-label="Encajar el plano" onClick={() => { setZoom(1); }}><Icono nombre="locate-fixed" /></button>
                            </span>
                        </div>
                    ) : (
                        <div className={`rv-sl-elegida${elegidos.length > 0 ? ' rv-sl-elegida--negra' : ''}`}>
                            <span className="rv-sl-elegida__texto">
                                <strong>{elegidos.length > 0 ? `${mayuscula(palabra)} para ${nombreDe(reserva, nombres)}` : nombreDe(reserva, nombres)}</strong>
                                <small>
                                    {elegidos.length > 0
                                        ? `${codigos(elegidos)} • ${String(capacidad)} sitios${capacidad > 0 && capacidad < reserva.party_size ? ' (no caben todos)' : ''}`
                                        : [horaEn(reserva.starts_at, contexto.zona), `${String(reserva.party_size)} pers.`, codigos(reserva.resource_ids ?? [])].filter((x) => x !== '').join(' • ')}
                                </small>
                                {ficha?.health_info != null && (
                                    <span className="rv-sl-alergias">
                                        <Icono nombre="triangle-alert" />
                                        <span>{`Alergias o intolerancias: ${ficha.health_info.text}`}</span>
                                    </span>
                                )}
                            </span>
                            <span className="rv-sl-elegida__botones">
                                <button type="button" className="rv-b rv-b-sec" onClick={salir}>
                                    <Icono nombre="x" />
                                    <span>{elegidos.length > 0 ? 'Cancelar' : 'Cerrar'}</span>
                                </button>
                                {permisos.puede(P.reservas) && elegidos.length === 0 && (
                                    <button type="button" className="rv-b rv-b-sec" onClick={() => { navegar(`/reservas?reserva=${encodeURIComponent(reserva.reservation_id)}`); }}>
                                        <Icono nombre="calendar-check" />
                                        <span>Ficha</span>
                                    </button>
                                )}
                                {puede.asignar && elegidos.length > 0 && (
                                    <button type="button" className="rv-b rv-b-sec" disabled={ocupado} onClick={() => { lanzar(reserva, ids, false); }}>
                                        <Icono nombre="arrow-right" />
                                        <span>Solo asignar</span>
                                    </button>
                                )}
                                {puede.llegada && esPorLlegar(reserva) && (ids.length > 0 || !puede.asignar) && (
                                    <button type="button" className="rv-b rv-b-pri" disabled={ocupado} onClick={() => { lanzar(reserva, elegidos, true); }}>
                                        <Icono nombre="armchair" />
                                        <span>Sentar</span>
                                    </button>
                                )}
                            </span>
                        </div>
                    )}
                    {reserva !== null && elegidos.length === 0 && puede.asignar && (reserva.resource_ids ?? []).length === 0 && (
                        <p className="rv-sl-nota">{`Toca un ${palabra} libre del plano (los que encajan están marcados). Para un grupo grande, toca varios y se juntan.`}</p>
                    )}
                    <PlanoSala
                        nombre={salaActual?.name ?? ''}
                        ancho={salaActual?.width ?? 1}
                        alto={salaActual?.height ?? 1}
                        recursos={enPlano}
                        decoracion={(version.content.decorations ?? []).filter((d) => d.room_ref === refSala || d.room_ref === salaActual?.name)}
                        zona={contexto.zona}
                        zoom={zoom}
                        encajan={encajan}
                        elegidos={new Set(elegidos)}
                        onRecurso={tocarRecurso}
                    />
                </section>
            </div>

            {recursoAbierto !== null && (
                <FichaRecurso
                    rp={recursoAbierto}
                    titulo={codigo(recursoAbierto.posicion.resource_id)}
                    zona={contexto.zona}
                    reten={retenDe(foto, recursoAbierto.posicion.resource_id)}
                    puede={puede}
                    ocupado={ocupado}
                    nombreZona={nombreZona}
                    onCerrar={() => { setRecursoAbierto(null); }}
                    onReten={() => { if (recursoAbierto.recurso !== undefined) { setReten({ recurso: recursoAbierto.recurso, error: null }); } setRecursoAbierto(null); }}
                    onQuitarReten={quitarReten}
                    onWalkIn={() => { setWalkIn({ recurso: recursoAbierto.posicion.resource_id }); setRecursoAbierto(null); }}
                />
            )}

            {walkIn !== null && (
                <WalkIn
                    abierto
                    movil={movil}
                    palabra={palabra}
                    subtitulo={horaEn(ahora, contexto.zona)}
                    libresPara={(n) => {
                        const libres = libresPara(foto, recursos, n);
                        const pedido = recursos.find((x) => x.id === walkIn.recurso);
                        return pedido !== undefined && !libres.includes(pedido) && cabe(pedido, n) ? [pedido, ...libres] : libres;
                    }}
                    nombreZona={nombreZona}
                    inicial={walkIn.recurso}
                    ocupado={ocupado}
                    onCerrar={() => { setWalkIn(null); }}
                    onSentar={({ personas, telefono, recurso }) => {
                        setOcupado(true);
                        ops.createWalkIn(negocio, { party_size: personas, resource_ids: [recurso], confirm_different_resource: false, ...(telefono === '' ? {} : { contact: { phone: telefono } }) })
                            .then(() => {
                                avisar({ tipo: 'ok', texto: `Walk-in sentado en ${codigo(recurso)}.` });
                                setWalkIn(null);
                            })
                            .catch((e: unknown) => { avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? (e instanceof ErrorApi ? e.message : 'No se ha podido sentar el walk-in.') }); })
                            .finally(() => {
                                setOcupado(false);
                                recargar();
                            });
                    }}
                />
            )}

            {reten !== null && (
                <VentanaReten
                    abierta
                    codigo={codigo(reten.recurso.id).toLocaleLowerCase('es').replace(/^/, 'el ')}
                    zona={contexto.zona}
                    ahora={ahora}
                    ocupado={ocupado}
                    error={reten.error}
                    onCerrar={() => { setReten(null); }}
                    onGuardar={(hasta, motivo) => {
                        setOcupado(true);
                        ops.createDoorHold(negocio, { resource_id: reten.recurso.id, expires_at: hasta, ...(motivo === '' ? {} : { reason: motivo }) })
                            .then(() => {
                                avisar({ tipo: 'ok', texto: `${codigo(reten.recurso.id)}, guardado para la puerta.` });
                                setReten(null);
                            })
                            .catch((e: unknown) => {
                                if (e instanceof ErrorApi && e.codigo === 'validation_failed') {
                                    setReten({ recurso: reten.recurso, error: 'Como mucho, hasta el final de la jornada.' });
                                } else {
                                    avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? 'No se ha podido guardar el retén.' });
                                }
                            })
                            .finally(() => {
                                setOcupado(false);
                                recargar();
                            });
                    }}
                />
            )}

            {escanear && (
                <VentanaEscanear
                    abierta
                    ocupado={ocupado}
                    resultado={lectura}
                    onCerrar={() => { setEscanear(false); }}
                    onLeido={(qr) => { leer({ method: 'qr', qr_payload: qr }); }}
                    onCodigo={(c) => { leer({ method: 'typed_code', code: c }); }}
                />
            )}

            {confirmar !== null && reserva !== null && (
                <VentanaConfirmar
                    confirmacion={confirmar.c}
                    sentar={confirmar.sentar}
                    ocupado={ocupado}
                    onCerrar={() => { setConfirmar(null); }}
                    onConfirmar={(motivo) => {
                        const c = confirmar;
                        setConfirmar(null);
                        const extra: Opciones = c.c.tipo === 'desplazar' ? { desplazar: true } : c.c.tipo === 'igualmente' ? { igualmente: true } : { motivo };
                        lanzar(reserva, ids, c.sentar, { ...c.opciones, ...extra });
                    }}
                />
            )}

            {imprimir && (
                <HojaOcupacion
                    dia={dia}
                    hoy={hoy}
                    foto={foto}
                    version={version}
                    recursos={recursos}
                    nombres={nombres}
                    contexto={contexto}
                    onDia={(d) => { navegar(`/sala?imprimir=1&dia=${d}`, true); }}
                    onCerrar={() => {
                        setImprimir(false);
                        if (busqueda.has('imprimir')) {
                            navegar('/sala', true);
                        }
                    }}
                />
            )}
        </div>
    );
}

interface Opciones {
    desplazar?: boolean;
    igualmente?: boolean;
    motivo?: string;
    clave?: string;
}

/** Un recurso del plano sin reserva elegida: libre (retén o walk-in aquí), retenido (quitar) o cerrado. */
function FichaRecurso({ rp, titulo, zona, reten, puede, ocupado, nombreZona, onCerrar, onReten, onQuitarReten, onWalkIn }: {
    rp: RecursoEnPlano;
    titulo: string;
    zona: string;
    reten: ReturnType<typeof retenDe>;
    puede: { retenes: boolean; walkIn: boolean };
    ocupado: boolean;
    nombreZona: (id: string) => string;
    onCerrar: () => void;
    onReten: () => void;
    onQuitarReten: (id: string) => void;
    onWalkIn: () => void;
}) {
    const t = rp.recurso?.current_terms;
    const detalle = t === undefined ? '' : `${nombreZona(t.zone_id)} • ${String(t.min_capacity)}–${String(t.max_capacity)} personas`;
    if (rp.estado === 'cerrado') {
        return <Ventana abierta titulo={`${titulo}: cerrado`} texto={`Está cerrado${rp.recurso?.active_closure?.ends_at == null ? '' : ` hasta las ${horaEn(rp.recurso.active_closure.ends_at, zona)}`}. Se reabre en Recursos.`} cancelar="Cerrar" accion="" onCerrar={onCerrar} />;
    }
    if (reten !== undefined) {
        return (
            <Ventana
                abierta
                titulo={`${titulo}: guardado para la puerta`}
                texto={`Hasta las ${horaEn(reten.expires_at, zona)}${reten.reason == null ? '' : ` · ${reten.reason}`}. La web no lo vende mientras tanto.`}
                cancelar="Cerrar"
                {...(puede.retenes ? { accion: 'Quitar el retén', accionIcono: 'x' as const, ocupado, onAccion: () => { onQuitarReten(reten.id); } } : { accion: '' })}
                onCerrar={onCerrar}
            >
                {puede.walkIn && (
                    <button type="button" className="rv-b rv-b-sec" onClick={onWalkIn}>
                        <Icono nombre="user-plus" />
                        <span>Sentar un walk-in aquí</span>
                    </button>
                )}
            </Ventana>
        );
    }
    return (
        <Ventana abierta titulo={`${titulo}: libre`} texto={detalle} cancelar="Cerrar" {...(puede.walkIn ? { accion: 'Walk-in aquí', accionIcono: 'user-plus' as const, onAccion: onWalkIn } : { accion: '' })} onCerrar={onCerrar}>
            {puede.retenes && (
                <button type="button" className="rv-b rv-b-sec" onClick={onReten}>
                    <Icono nombre="door-open" />
                    <span>Guardar para la puerta</span>
                </button>
            )}
        </Ventana>
    );
}
